import { assertNever } from '@ofmos/shared/exhaustive';
import { type FilterOperator, fieldTypeDef, isNullaryOperator } from '@ofmos/shared/field-types';
import type {
  FilterAnchor,
  FilterCombinator,
  RelationCountComparator,
  RelationDirection,
} from '@ofmos/shared/filter-ast';
import { canonicaliseRelationCount, FilterStructureError } from '@ofmos/shared/filter-ast';
import {
  FILTER_OPERATOR_LABELS,
  RELATIVE_DATE_LABELS as SHARED_RELATIVE_DATE_LABELS,
} from '@ofmos/shared/filter-labels';
import {
  isRelativeDateOperand,
  RELATIVE_DATE_OPERATOR,
  RELATIVE_DATE_TOKENS,
  type RelativeDateToken,
} from '@ofmos/shared/view';
import type { FieldMetadata, ViewFilterNode } from '../../data/client';
import { fieldShape } from '../../fields/fieldTypeMeta';

/**
 * THE FILTER EDITOR'S MODEL (D20): a FLAT list of conditions joined by ONE combinator,
 * and the inverse that reads a stored filter back into it.
 *
 * Three things changed here from the one-condition version, and each one is a thing Ben
 * cannot do without it:
 *
 *  - MORE THAN ONE CONDITION. "Due this week AND not paid" is the Payments view, and the
 *    editor capped at one. Still FLAT and still one combinator: the AST supports
 *    arbitrary trees (INV-6) and a tree BUILDER is a screen of its own.
 *  - A RELATION OPERAND, so "Creators where Agency is Luna Media" is expressible at all.
 *  - A MULTI-VALUE SELECT OPERAND (`in` / `notIn`), because D22's Agencies view is
 *    `lifecycle_status in (onboarding, active)` - and without it a SEEDED DEFAULT VIEW
 *    would ship permanently read-only.
 *
 * WHAT STAYS OUT, with the original reasoning intact: CURRENCY (an amount plus a code),
 * MULTI_SELECT (a set compared against a set) and DATETIME (an instant with an offset)
 * each need their own editor, and half-supporting them is worse than not offering them.
 * Both backends already handle all three, so adding an editor later changes this file
 * and nothing else.
 *
 * THE INVERSE IS PARTIAL ON PURPOSE (`fromViewFilter` returns null). A filter this
 * editor cannot REPRESENT must never be silently rewritten into one it can - a view
 * saved by an API client, or by a later version of this editor, would be quietly
 * destroyed by whoever opened it next. The caller renders such a filter as a read-only
 * sentence instead.
 */
export type FilterInputKind =
  | 'text'
  | 'number'
  | 'boolean'
  | 'option'
  /** `in` / `notIn` over a SELECT: a checkbox list over the same options (D20). */
  | 'optionList'
  | 'date'
  /** `between` over a DATE: two dates, or one of D17's relative tokens. */
  | 'dateRange'
  /** A record picker over the target object, searching (D5's picker, reused). */
  | 'relation'
  | 'none';

export function filterInputKind(field: FieldMetadata, operator: FilterOperator): FilterInputKind {
  if (isNullaryOperator(operator)) return 'none';
  const shape = fieldShape(field.type);
  switch (shape) {
    case 'text':
      return 'text';
    case 'number':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'option':
      return operator === 'in' || operator === 'notIn' ? 'optionList' : 'option';
    case 'date':
      // DATE only; DATETIME is excluded by `isFilterableField`.
      return operator === 'between' ? 'dateRange' : 'date';
    case 'relation':
      return 'relation';
    case 'options':
    case 'money':
      return 'none';
    /**
     * FILES (`file-attachments.md` D11). UNREACHABLE IN PRACTICE and required to compile:
     * this function short-circuits to `'none'` for a nullary operator on its first line, and
     * FILES declares nothing BUT `isEmpty` and `isNotEmpty`. The arm exists because the
     * switch is over `FieldShape` rather than over the operator, and an `assertNever` that
     * cannot narrow is a build failure rather than a runtime one.
     */
    case 'files':
      return 'none';
    default:
      return assertNever(shape, 'filterInputKind');
  }
}

/**
 * Which fields the editor offers.
 *
 * RELATION is IN now - a MANY_TO_ONE owns a real uuid column and `ENUMERABLE_OPERATORS`
 * includes `eq`, so "Calls where Agency is this one" is an ordinary condition. A
 * MANY_TO_MANY is not: it owns no column, and a filter on one would be refused by the
 * planner with `not_supported`.
 */
export function isFilterableField(field: FieldMetadata): boolean {
  const shape = fieldShape(field.type);
  /**
   * FILES IS FILTERABLE AND FALLS THROUGH TO `true` (`file-attachments.md` D11): it supports
   * `isEmpty` and `isNotEmpty`, so "agencies with no contract on file" is an ordinary
   * condition and belongs in the picker.
   *
   * The right answer arrived by ACCIDENT - a new shape falls through this function's final
   * `return true` - so it is pinned as deliberate rather than left as a coincidence. That
   * pin is obligation 42's neighbour and lives in the field-type map test.
   */
  if (shape === 'options' || shape === 'money') return false;
  if (field.type === 'DATETIME') return false;
  if (shape === 'relation') return field.options?.relation?.kind === 'MANY_TO_ONE';
  return true;
}

export function operatorsFor(field: FieldMetadata): readonly FilterOperator[] {
  return fieldTypeDef(field.type).filterOperators;
}

/**
 * Human labels. The AST's names are precise, not readable.
 *
 * THEY MOVED TO `shared` when the workflow builder arrived: a workflow's ONLY IF card and
 * its generated one-line summary read a condition in the same eighteen words this toolbar
 * does, and the summary is rendered on the SERVER as well as in the browser. Re-exported
 * here so every existing call site is unchanged and there is still exactly one list.
 */
export const OPERATOR_LABELS: Readonly<Record<FilterOperator, string>> = FILTER_OPERATOR_LABELS;

/** D17's tokens, in the words a person reads rather than the words the AST stores. */
export const RELATIVE_DATE_LABELS: Readonly<Record<RelativeDateToken, string>> =
  SHARED_RELATIVE_DATE_LABELS;

export interface FilterCondition {
  readonly kind: 'condition';
  /** Local identity for React keys and for editing one row of a flat list. */
  readonly id: string;
  readonly fieldId: string;
  readonly operator: FilterOperator;
  /** Single-operand types. Raw input text; typed for the AST by `toViewFilter`. */
  readonly value: string;
  /** `in` / `notIn` option ids, and the two ends of a fixed `between`. */
  readonly values: readonly string[];
  /** D17: set instead of `values` when a DATE range is relative rather than fixed. */
  readonly relative: RelativeDateToken | null;
}

/**
 * THE SEVEN EDITOR PHRASES (`docs/decisions/relation-filters.md` D28, section 11).
 *
 * Two of them are quantifiers on their own (`has any`, `has none`) and five are a
 * quantifier plus a comparator plus a number. They are a UI vocabulary rather than a wire
 * one: `has at least` and `has more than` are both `quantifier: 'count'` on the wire, and
 * `has at least 1` is not stored at all - it CANONICALISES to `has any` on the way out
 * (obligation 70), which is how D9's "two spellings for one question" objection is
 * answered rather than ignored.
 */
export type RelationPhrase =
  | 'any'
  | 'none'
  | 'atLeast'
  | 'moreThan'
  | 'fewerThan'
  | 'atMost'
  | 'exactly';

export const RELATION_PHRASES: readonly {
  readonly value: RelationPhrase;
  readonly label: string;
}[] = [
  { value: 'any', label: 'has any' },
  { value: 'none', label: 'has none' },
  { value: 'atLeast', label: 'has at least' },
  { value: 'moreThan', label: 'has more than' },
  { value: 'fewerThan', label: 'has fewer than' },
  { value: 'atMost', label: 'has at most' },
  { value: 'exactly', label: 'has exactly' },
];

/** The five phrases that take a number, and the comparator each one means. */
const PHRASE_COMPARATORS = {
  atLeast: 'gte',
  moreThan: 'gt',
  fewerThan: 'lt',
  atMost: 'lte',
  exactly: 'eq',
} as const satisfies Readonly<Record<string, RelationCountComparator>>;

type CountingPhrase = keyof typeof PHRASE_COMPARATORS;

export function phraseTakesNumber(phrase: RelationPhrase): phrase is CountingPhrase {
  return phrase !== 'any' && phrase !== 'none';
}

/** The words a sentence uses, which are not the words the select uses ("has at least" -> "at least"). */
const PHRASE_WORDS: Readonly<Record<CountingPhrase, string>> = {
  atLeast: 'at least',
  moreThan: 'more than',
  fewerThan: 'fewer than',
  atMost: 'at most',
  exactly: 'exactly',
};

/**
 * ONE RELATED ROW (D21), and it is a FOURTH ROW SHAPE beside the three the editor already
 * has rather than a second editor.
 *
 * ```
 * [ Related records v ] [ has any v ] [ n ] [ field v ] [ operator v ] [ operand ]
 * ```
 *
 * EXACTLY ONE INNER CONDITION, which is D21's decided limit: two inner conditions is a
 * nested tree in a flat editor, and a filter this editor cannot represent renders as the
 * read-only sentence instead of being silently rewritten.
 *
 * `nested` IS THE SECOND HOP, and it is the one place this row is a small tree. D7 caps a
 * filter at two hops and D2 puts a junction's far object on the second one, so without it
 * the N:N half of this module could ask about a membership's own fields and never about
 * the person - which is the half D2 calls "half of the module's promise". A nested row
 * carries no nested row of its own; the cap is structural.
 */
export interface FilterRelatedRow {
  readonly kind: 'related';
  readonly id: string;
  /**
   * WHICH ENTRY OF D27's ANCHOR SET, as one string, because a self-relation contributes
   * TWO entries for one field id (D2: an anchor an object declares at itself satisfies
   * both halves of the derivation, so the direction cannot be derived and must be stated).
   * `<fieldId>` where the direction is derived, `<fieldId>:forward` / `<fieldId>:reverse`
   * where it is not.
   */
  readonly anchorKey: string;
  readonly phrase: RelationPhrase;
  /** Raw input text, typed on the way out - an unparseable number yields no node. */
  readonly count: string;
  readonly inner: FilterCondition | null;
  readonly nested: FilterRelatedRow | null;
}

export type FilterRow = FilterCondition | FilterRelatedRow;

/**
 * THE MODEL IS ONE ORDERED LIST, and the order is not cosmetic.
 *
 * It used to be `conditions`, which could not hold a second row shape without either a
 * second array or a union - and a second array loses the ORDER a stored group has, so a
 * view saved as `[related, condition]` would read back as `[condition, related]`,
 * `toViewFilter` would produce different bytes, and `isDirty` would report unsaved changes
 * on a page nobody touched.
 */
export interface FilterModel {
  readonly combinator: FilterCombinator;
  readonly rows: readonly FilterRow[];
}

export const EMPTY_FILTER_MODEL: FilterModel = { combinator: 'and', rows: [] };

export function newCondition(field: FieldMetadata, id: string): FilterCondition {
  const operators = operatorsFor(field);
  return {
    kind: 'condition',
    id,
    fieldId: field.id,
    // The first operator a type declares is its most useful one (`eq` / `contains`).
    operator: operators[0] ?? 'eq',
    value: '',
    values: [],
    relative: null,
  };
}

/** `any` is the default and the only quantifier the editor pre-selects (D3). */
export function newRelatedRow(anchor: FilterAnchor, id: string): FilterRelatedRow {
  return {
    kind: 'related',
    id,
    anchorKey: anchorKeyOf(anchor),
    phrase: 'any',
    count: '1',
    inner: null,
    nested: null,
  };
}

// --- D27's anchor set, as the editor reads it -------------------------------

/**
 * WHAT THE EDITOR NEEDS BESIDES THIS OBJECT'S FIELDS, in one parameter.
 *
 * `anchors` is D27's published set, VERBATIM - never a union the browser computes, which
 * is what M3 found could not reach a hidden junction at all. `fieldsOf` answers "what may
 * an inner condition on the related object compare", and it answers `undefined` while that
 * object's fields are still in flight, which every caller treats as "not yet" rather than
 * as "none".
 */
export interface RelationContext {
  /** The object the filter is ON, which is the object whose anchor set the top row reads. */
  readonly objectId: string;
  /**
   * D27's published set for ONE object, VERBATIM - never a union the browser computes,
   * which is what M3 found could not reach a hidden junction at all. `undefined` means the
   * read has not landed: every caller treats that as "not yet", never as "none".
   *
   * Keyed by object rather than fixed to one, because the SECOND hop's picker is the same
   * question asked about the related object (D7's depth 2).
   */
  anchorsOf(objectId: string): readonly FilterAnchor[] | undefined;
  /** What an inner condition on that object may compare, or `undefined` while it loads. */
  fieldsOf(objectId: string): readonly FieldMetadata[] | undefined;
}

export const EMPTY_RELATION_CONTEXT: RelationContext = {
  objectId: '',
  anchorsOf: () => undefined,
  fieldsOf: () => undefined,
};

/**
 * WHICH OBJECTS A FILTER WILL WANT TO KNOW ABOUT, expressed WITHOUT a context.
 *
 * This is the half that breaks the circle. Resolving an anchor id into a related object
 * needs D27's read; deciding WHICH reads to issue therefore cannot need it, or nothing
 * ever loads. Both a stored node and a live draft can name their anchor FIELD ids with no
 * metadata at all, so that is what travels: the hook resolves them as each level arrives.
 */
export interface RelationNeed {
  readonly anchorFieldId: string;
  /** Anchor field ids ONE HOP IN, which is where D7's second level lives. */
  readonly nested: readonly string[];
}

export function relationNeedsOfNode(node: ViewFilterNode | null): readonly RelationNeed[] {
  if (node === null) return [];
  if (node.kind === 'group') return node.children.flatMap(relationNeedsOfNode);
  if (node.kind !== 'related') return [];
  return [
    {
      anchorFieldId: node.anchorFieldId,
      nested: node.children.flatMap((child) =>
        child.kind === 'related' ? [child.anchorFieldId] : [],
      ),
    },
  ];
}

export function relationNeedsOfModel(model: FilterModel): readonly RelationNeed[] {
  return model.rows.flatMap((row) =>
    row.kind === 'related'
      ? [
          {
            anchorFieldId: anchorFieldIdOf(row.anchorKey),
            nested: row.nested === null ? [] : [anchorFieldIdOf(row.nested.anchorKey)],
          },
        ]
      : [],
  );
}

export function anchorFieldIdOf(anchorKey: string): string {
  return anchorKey.split(':')[0] ?? anchorKey;
}

export function anchorKeyOf(anchor: FilterAnchor): string {
  return anchor.direction === 'ambiguous'
    ? `${anchor.anchorFieldId}:forward`
    : anchor.anchorFieldId;
}

/**
 * A SELF-RELATION IS TWO ENTRIES IN THE PICKER, not one entry plus a direction control.
 *
 * D2 requires the direction to be STATED for an anchor an object declares at itself, and
 * the two directions are two different questions ("people who HAVE a manager" and "people
 * who ARE one"). Offering them as two rows in the list a person is already reading is one
 * decision instead of two, and it keeps the row's shape identical for every anchor.
 */
export interface AnchorChoice {
  readonly key: string;
  readonly label: string;
  readonly anchor: FilterAnchor;
  readonly direction: RelationDirection | null;
}

export function anchorChoices(anchors: readonly FilterAnchor[]): readonly AnchorChoice[] {
  const choices: AnchorChoice[] = [];
  for (const anchor of anchors) {
    if (anchor.direction === 'ambiguous') {
      choices.push({
        key: `${anchor.anchorFieldId}:forward`,
        label: anchor.anchorFieldLabel,
        anchor,
        direction: 'forward',
      });
      choices.push({
        key: `${anchor.anchorFieldId}:reverse`,
        label: `${anchor.label} by ${anchor.anchorFieldLabel}`,
        anchor,
        direction: 'reverse',
      });
      continue;
    }
    choices.push({ key: anchor.anchorFieldId, label: anchor.label, anchor, direction: null });
  }
  /**
   * TWO ANCHORS CAN PRINT THE SAME WORD, and the server cannot see it coming: an object
   * with two MANY_TO_ONE fields pointing at this one ("Deals" by owner and "Deals" by
   * partner) yields two entries labelled "Deals". A picker with two identical rows is a
   * coin toss, so the colliding ones - and only the colliding ones - name their field.
   */
  const seen = new Map<string, number>();
  for (const choice of choices) seen.set(choice.label, (seen.get(choice.label) ?? 0) + 1);
  return choices.map((choice) =>
    (seen.get(choice.label) ?? 0) > 1
      ? { ...choice, label: `${choice.label} (${choice.anchor.anchorFieldLabel})` }
      : choice,
  );
}

export interface AnchorResolution {
  readonly anchor: FilterAnchor;
  /** Stated only where D2 cannot derive it, which is a self-relation and nothing else. */
  readonly direction: RelationDirection | null;
}

export function choiceFor(
  relations: RelationContext,
  onObjectId: string,
  anchorKey: string,
): AnchorResolution | undefined {
  const [fieldId, stated] = anchorKey.split(':');
  const anchor = relations.anchorsOf(onObjectId)?.find((entry) => entry.anchorFieldId === fieldId);
  if (anchor === undefined) return undefined;
  if (anchor.direction === 'ambiguous') {
    if (stated !== 'forward' && stated !== 'reverse') return undefined;
    return { anchor, direction: stated };
  }
  return stated === undefined ? { anchor, direction: null } : undefined;
}

/**
 * WHY THIS ROW CANNOT BE APPLIED, as the sentence section 11 wrote for it - or `null`.
 *
 * Two of the seven phrases can be typed into a comparison that cannot narrow, and D28
 * REFUSES both rather than storing them: "at least 0" is true for every record and "fewer
 * than 0" is true for none. The canonicaliser throws for exactly those two, in `shared`,
 * for all three of its callers - so this function asks IT rather than restating the rule.
 */
export function relatedRowIssue(row: FilterRelatedRow, subject: string): string | null {
  if (!phraseTakesNumber(row.phrase)) return null;
  const n = Number(row.count);
  if (!Number.isInteger(n) || n < 0) return null;
  try {
    canonicaliseRelationCount({
      quantifier: 'count',
      count: { cmp: PHRASE_COMPARATORS[row.phrase], n },
    });
    return null;
  } catch (error) {
    if (!(error instanceof FilterStructureError)) throw error;
    return row.phrase === 'atLeast'
      ? `"At least 0 ${subject}" is true for every record. Choose a number above zero, or use "has any".`
      : `"Fewer than 0 ${subject}" is true for no record. Choose a number above zero, or use "has none".`;
  }
}

/**
 * THE DEGENERATE PICK, named out loud before Apply renames it (section 11).
 *
 * `has at least 1` and `has any` are the same question with different costs, so the module
 * removes the choice: the canonicaliser rewrites one into the other and the row re-reads as
 * the simpler phrase. That is visible and deliberate, and a hint is what makes it legible
 * rather than surprising.
 */
export function relatedRowHint(row: FilterRelatedRow): string | null {
  if (!phraseTakesNumber(row.phrase)) return null;
  const n = Number(row.count);
  if (!Number.isInteger(n) || n < 0) return null;
  const canonical = canonicaliseRelationCountSafely(PHRASE_COMPARATORS[row.phrase], n);
  if (canonical === 'any') {
    return `"${labelOfPhrase(row.phrase)} ${n}" is the same question as "has any", so this filter was simplified.`;
  }
  if (canonical === 'none') {
    return `"${labelOfPhrase(row.phrase)} ${n}" is the same question as "has none", so this filter was simplified.`;
  }
  return null;
}

/** "at least" as a sentence starts: the same word, capitalised, and nowhere else. */
function labelOfPhrase(phrase: CountingPhrase): string {
  const word = PHRASE_WORDS[phrase];
  return `${word.charAt(0).toUpperCase()}${word.slice(1)}`;
}

function canonicaliseRelationCountSafely(
  cmp: RelationCountComparator,
  n: number,
): 'any' | 'none' | 'count' | null {
  try {
    return canonicaliseRelationCount({ quantifier: 'count', count: { cmp, n } }).quantifier;
  } catch {
    return null;
  }
}

/**
 * The model as a stored filter, or `null` when nothing usable has been entered yet.
 *
 * Typing happens HERE and only here: the AST carries typed operands, and an input
 * element only ever produces strings. An unparseable number yields no condition rather
 * than `NaN`, because a filter the server would refuse must never leave the browser.
 *
 * ONE condition produces a bare condition rather than a group of one. Both are legal and
 * mean the same thing, but the bare form is what a hand-written filter looks like, and
 * keeping the shapes identical is what makes `fromViewFilter(toViewFilter(x))` a round
 * trip rather than an approximation.
 */
export function toViewFilter(
  model: FilterModel,
  fields: readonly FieldMetadata[],
  relations: RelationContext = EMPTY_RELATION_CONTEXT,
): ViewFilterNode | null {
  const byId = new Map(fields.map((field) => [field.id, field]));
  const nodes = model.rows.flatMap((row) => {
    const node =
      row.kind === 'related'
        ? toRelated(row, relations, relations.objectId)
        : toCondition(row, byId.get(row.fieldId));
    return node === null ? [] : [node];
  });
  if (nodes.length === 0) return null;
  if (nodes.length === 1) return nodes[0] as ViewFilterNode;
  return { kind: 'group', combinator: model.combinator, children: nodes };
}

/**
 * A RELATED ROW AS A STORED NODE - CANONICAL, which is obligation 62's editor share.
 *
 * `canonicaliseRelationCount` is the SAME pure function `checkAndNormalise` and
 * `planRelatedNodes` call, imported from `shared` rather than re-derived here, so all
 * three doors produce identical nodes for identical input (D28, M14). The two rows of its
 * table that cannot narrow throw, and this returns `null` for them: an unappliable row
 * yields no node, exactly as an unparseable number does, and the row itself carries the
 * sentence (`relatedRowIssue`).
 */
function toRelated(
  row: FilterRelatedRow,
  relations: RelationContext,
  onObjectId: string,
): ViewFilterNode | null {
  const choice = choiceFor(relations, onObjectId, row.anchorKey);
  if (choice === undefined) return null;

  let quantifier: ViewRelated['quantifier'] = 'any';
  let count: ViewRelated['count'];
  if (row.phrase === 'none') {
    quantifier = 'none';
  } else if (phraseTakesNumber(row.phrase)) {
    const n = Number(row.count);
    if (!Number.isInteger(n) || n < 0 || row.count.trim() === '') return null;
    try {
      const canonical = canonicaliseRelationCount({
        quantifier: 'count',
        count: { cmp: PHRASE_COMPARATORS[row.phrase], n },
      });
      quantifier = canonical.quantifier;
      count = canonical.count;
    } catch (error) {
      if (!(error instanceof FilterStructureError)) throw error;
      return null;
    }
  }

  /**
   * THE INNER CONDITION RESOLVES AGAINST THE RELATED OBJECT'S FIELDS, never this one's.
   * `undefined` means those fields have not arrived yet, and the honest answer then is the
   * EXISTENCE node the row already means - not a condition typed against the wrong object.
   */
  /**
   * A ROW WHOSE INNER CONDITION CANNOT BE BUILT PRODUCES **NO NODE**, and never an existence
   * one. Dropping the condition and keeping the hop would turn "has any Deals where Stage is
   * Open" into "has any Deals" - which is D19's own sentence about the other direction: a
   * chip that says something the user never asked for. Both outcomes widen the list; only
   * one of them is VISIBLE, and it is the one where the chip disappears.
   *
   * Two ways to get here and both are real: the related object's fields have not landed yet
   * (`fieldsOf` answers `undefined`), and an operand the user has not finished typing - which
   * is the same thing the scalar row already does with a half-typed condition.
   */
  const relatedFields = relations.fieldsOf(choice.anchor.relatedObjectId);
  const children: ViewFilterNode[] = [];
  if (row.nested !== null) {
    const nested = toRelated(row.nested, relations, choice.anchor.relatedObjectId);
    if (nested === null) return null;
    children.push(nested);
  } else if (row.inner !== null) {
    if (relatedFields === undefined) return null;
    const innerField = relatedFields.find((field) => field.id === row.inner?.fieldId);
    const inner = toCondition(row.inner, innerField);
    if (inner === null) return null;
    children.push(inner);
  }

  return {
    kind: 'related',
    anchorFieldId: choice.anchor.anchorFieldId,
    quantifier,
    ...(choice.direction === null ? {} : { direction: choice.direction }),
    ...(count === undefined ? {} : { count }),
    children,
  };
}

type ViewRelated = Extract<ViewFilterNode, { kind: 'related' }>;

function toCondition(
  condition: FilterCondition,
  field: FieldMetadata | undefined,
): ViewFilterNode | null {
  if (field === undefined) return null;
  if (isNullaryOperator(condition.operator)) {
    return { kind: 'condition', fieldId: field.id, operator: condition.operator };
  }

  switch (filterInputKind(field, condition.operator)) {
    case 'number': {
      const parsed = Number(condition.value);
      return Number.isFinite(parsed) && condition.value.trim() !== ''
        ? { kind: 'condition', fieldId: field.id, operator: condition.operator, value: parsed }
        : null;
    }
    case 'boolean':
      return condition.value === ''
        ? null
        : {
            kind: 'condition',
            fieldId: field.id,
            operator: condition.operator,
            value: condition.value === 'true',
          };
    case 'optionList':
      return condition.values.length === 0
        ? null
        : {
            kind: 'condition',
            fieldId: field.id,
            operator: condition.operator,
            value: [...condition.values],
          };
    case 'dateRange': {
      if (condition.relative !== null) {
        /**
         * D17: the TOKEN decides the operator, so a stored `eq` can never produce a
         * range operand the normalizer would reject deep inside a query.
         */
        return {
          kind: 'condition',
          fieldId: field.id,
          operator: RELATIVE_DATE_OPERATOR,
          value: { relative: condition.relative },
        };
      }
      const [from, to] = condition.values;
      return from === undefined || to === undefined || from === '' || to === ''
        ? null
        : { kind: 'condition', fieldId: field.id, operator: condition.operator, value: [from, to] };
    }
    case 'none':
      return null;
    default:
      // text, option, date and relation all compare as their string form: an option id,
      // a `YYYY-MM-DD`, or the target record's uuid.
      return condition.value === ''
        ? null
        : {
            kind: 'condition',
            fieldId: field.id,
            operator: condition.operator,
            value: condition.value,
          };
  }
}

/**
 * A STORED FILTER, BACK INTO THE EDITOR - or `null` when this editor cannot represent it
 * faithfully.
 *
 * `null` is the important answer. A filter written by an API client, by Oliver, or by a
 * later version of this editor may be a nested tree, an OR of ANDs, a CURRENCY
 * comparison or an operand shape nothing here models. Coercing one into the flat model
 * would mean the next Save silently destroyed it - so the caller renders it as a
 * read-only sentence with "Clear" and "Save as new view" instead, and the stored filter
 * survives being looked at.
 */
export function fromViewFilter(
  node: ViewFilterNode | null,
  fields: readonly FieldMetadata[],
  relations: RelationContext = EMPTY_RELATION_CONTEXT,
): FilterModel | null {
  if (node === null) return EMPTY_FILTER_MODEL;
  const byId = new Map(fields.map((field) => [field.id, field]));

  const row = fromRow(node, byId, relations, relations.objectId, 'c0');
  if (node.kind !== 'group') return row === null ? null : { combinator: 'and', rows: [row] };

  // A negated group is a set complement over the RESULT SET, which the flat editor has
  // no control for and must not silently drop.
  if (node.negate === true) return null;

  const rows: FilterRow[] = [];
  for (const [index, child] of node.children.entries()) {
    const built = fromRow(child, byId, relations, relations.objectId, `c${index}`);
    if (built === null) return null;
    rows.push(built);
  }
  return { combinator: node.combinator, rows };
}

/**
 * ONE STORED NODE AS ONE EDITOR ROW, or `null` when this editor cannot represent it.
 *
 * THE RELATED ARM IS PLACED BEFORE THE GROUP BRANCH ON PURPOSE (D21, C2). A related node
 * also carries `children`, so without an arm of its own an EXISTENCE-ONLY node - the
 * module's headline shape - fell into a loop written for group children, found none, and
 * came back as a NON-NULL, EMPTY model: the read-only sentence never rendered, the toolbar
 * showed no chip, and the next Save wrote the filter away.
 *
 * WAVE 1 ANSWERED `null` HERE and every related view opened read-only. This is the other
 * half of obligation 35: a shape the row can hold comes back as a ROW, and a shape it
 * cannot - two inner conditions, a third hop, an anchor the published set does not carry -
 * still comes back as `null`, which is the containment D21 leans on rather than a gap in it.
 */
function fromRow(
  node: ViewFilterNode,
  byId: ReadonlyMap<string, FieldMetadata>,
  relations: RelationContext,
  onObjectId: string,
  id: string,
): FilterRow | null {
  if (node.kind === 'related') return fromRelated(node, relations, onObjectId, id);
  if (node.kind === 'group') return null;
  return fromCondition(node, byId.get(node.fieldId), id);
}

function fromRelated(
  node: ViewRelated,
  relations: RelationContext,
  onObjectId: string,
  id: string,
): FilterRelatedRow | null {
  const anchors = relations.anchorsOf(onObjectId);
  /**
   * NOT KNOWN YET IS NOT THE SAME AS NOT LEGAL, and answering `null` for it would be the
   * read-only sentence flashing on every load until D27's read lands. The row is built
   * from the stored node either way; what the anchor set decides is only whether the
   * PICKER can name it, which is a render-time question.
   */
  const anchor = anchors?.find((entry) => entry.anchorFieldId === node.anchorFieldId);
  if (anchors !== undefined && anchor === undefined) return null;
  if (anchor?.direction === 'ambiguous' && node.direction === undefined) return null;
  if (anchor !== undefined && anchor.direction !== 'ambiguous' && node.direction !== undefined) {
    return null;
  }

  const phrase = phraseOf(node);
  if (phrase === null) return null;

  const anchorKey =
    node.direction === undefined ? node.anchorFieldId : `${node.anchorFieldId}:${node.direction}`;
  const base = {
    kind: 'related',
    id,
    anchorKey,
    phrase: phrase.phrase,
    count: String(phrase.n),
  } as const;

  if (node.children.length === 0) return { ...base, inner: null, nested: null };
  if (node.children.length > 1) return null;
  const child = node.children[0] as ViewFilterNode;

  if (child.kind === 'related') {
    if (anchor === undefined) return null;
    const nested = fromRelated(child, relations, anchor.relatedObjectId, `${id}-n`);
    return nested === null ? null : { ...base, inner: null, nested };
  }
  if (child.kind === 'group') return null;

  const relatedFields =
    anchor === undefined ? undefined : relations.fieldsOf(anchor.relatedObjectId);
  if (relatedFields === undefined) return null;
  const inner = fromCondition(
    child,
    relatedFields.find((field) => field.id === child.fieldId),
    `${id}-i`,
  );
  return inner === null ? null : { ...base, inner, nested: null };
}

/** The stored quantifier as one of the seven editor phrases, or `null` for `neq`. */
function phraseOf(
  node: ViewRelated,
): { readonly phrase: RelationPhrase; readonly n: number } | null {
  if (node.quantifier === 'any') return { phrase: 'any', n: 1 };
  if (node.quantifier === 'none') return { phrase: 'none', n: 1 };
  const count = node.count;
  if (count === undefined) return null;
  const phrase = (Object.keys(PHRASE_COMPARATORS) as CountingPhrase[]).find(
    (candidate) => PHRASE_COMPARATORS[candidate] === count.cmp,
  );
  /**
   * `neq` HAS NO EDITOR PHRASE and that is deliberate rather than an oversight: section 11
   * writes seven phrases and "not exactly n" is not one of them. A stored `count neq n`
   * is a shape the API can express and this editor cannot, so it takes the read-only
   * branch - which is the containment D21 already relies on for a nested tree.
   */
  return phrase === undefined ? null : { phrase, n: count.n };
}

function fromCondition(
  node: Extract<ViewFilterNode, { kind: 'condition' }>,
  field: FieldMetadata | undefined,
  id: string,
): FilterCondition | null {
  if (field === undefined || !isFilterableField(field)) return null;
  if (!operatorsFor(field).includes(node.operator)) return null;

  const base = { kind: 'condition', id, fieldId: field.id, operator: node.operator } as const;
  if (isNullaryOperator(node.operator)) {
    return { ...base, value: '', values: [], relative: null };
  }

  switch (filterInputKind(field, node.operator)) {
    case 'number':
      return typeof node.value === 'number'
        ? { ...base, value: String(node.value), values: [], relative: null }
        : null;
    case 'boolean':
      return typeof node.value === 'boolean'
        ? { ...base, value: node.value ? 'true' : 'false', values: [], relative: null }
        : null;
    case 'optionList':
      return Array.isArray(node.value) && node.value.every((entry) => typeof entry === 'string')
        ? { ...base, value: '', values: node.value as readonly string[], relative: null }
        : null;
    case 'dateRange': {
      if (isRelativeDateOperand(node.value)) {
        return { ...base, value: '', values: [], relative: node.value.relative };
      }
      return Array.isArray(node.value) &&
        node.value.length === 2 &&
        node.value.every((entry) => typeof entry === 'string')
        ? { ...base, value: '', values: node.value as readonly string[], relative: null }
        : null;
    }
    case 'none':
      return null;
    default:
      return typeof node.value === 'string'
        ? { ...base, value: node.value, values: [], relative: null }
        : null;
  }
}

/**
 * What the toolbar button says, read off the NODE rather than off a draft.
 *
 * The node is the thing that survives a reload, arrives from a saved view and comes back
 * from the server; describing the draft instead meant the button could only ever
 * describe a filter this session had typed. A relation names the RECORD it points at
 * only if the caller can resolve it, so the id is deliberately not printed - "Agency is
 * set" beats a uuid nobody can read.
 */
export function describeViewFilter(
  node: ViewFilterNode | null,
  fields: readonly FieldMetadata[],
  relations: RelationContext = EMPTY_RELATION_CONTEXT,
): string {
  return sentenceOf(describeViewFilterParts(node, fields, relations));
}

/**
 * THE SAME SENTENCE IN ITS TWO HALVES, for the one caller that has to decide which half
 * survives a narrow box: the list's filter token (P4 wave 3 round 2, brief item 2).
 *
 * WHY THE SPLIT LIVES HERE and not in the token. A token that cut its own label by
 * searching the string for the operator would be a second grammar for the same sentence,
 * which is what `filterTokens.ts` already refuses to have; the sentence is composed in
 * this file, so this is the only place that knows where the field's name ends. The token
 * then never truncates its head and truncates the operand instead: "Status is one of ..."
 * still says what it narrows, where the 81px "S.." the parent photographed said nothing.
 */
export interface FilterSentence {
  /** The field's name and the operator: the part a narrow token keeps whole. */
  readonly head: string;
  /** The operand, which is what gives way first. `undefined` for a nullary operator. */
  readonly value: string | undefined;
}

export function describeViewFilterParts(
  node: ViewFilterNode | null,
  fields: readonly FieldMetadata[],
  relations: RelationContext = EMPTY_RELATION_CONTEXT,
): FilterSentence {
  if (node === null) return { head: 'Filter', value: undefined };
  const byId = new Map(fields.map((field) => [field.id, field]));
  const parts = describeParts(node, byId, relations, relations.objectId);
  if (parts.length === 0) return { head: 'Filter', value: undefined };
  if (parts.length === 1) return parts[0] as FilterSentence;
  // Two is readable; more is a count, because a toolbar button is not a paragraph.
  return { head: `${parts.length} filters`, value: undefined };
}

function sentenceOf(parts: FilterSentence): string {
  return parts.value === undefined ? parts.head : `${parts.head} ${parts.value}`;
}

function describeParts(
  node: ViewFilterNode,
  byId: ReadonlyMap<string, FieldMetadata>,
  relations: RelationContext,
  onObjectId: string,
): readonly FilterSentence[] {
  if (node.kind === 'group') {
    return node.children.flatMap((child) => describeParts(child, byId, relations, onObjectId));
  }
  if (node.kind === 'related') return [describeRelated(node, relations, onObjectId)];

  const field = byId.get(node.fieldId);
  if (field === undefined) return [{ head: '1 filter on a field that is gone', value: undefined }];
  return [describeCondition(node, field)];
}

/**
 * THE CHIP SENTENCE FOR A RELATED NODE, in section 11's words rather than in the AST's.
 *
 * "Has any Deals where Stage is Open", "Has no Deals", "Has more than 3 Deals where Stage
 * is Open", "Has exactly 1 Deal where Stage is Won", "Agency where City is Berlin". The
 * quantifier picks the opening, the anchor names the subject and the one inner condition -
 * or the one nested hop - becomes the `where` clause.
 *
 * IT DEGRADES RATHER THAN LYING when D27's read or the related object's fields have not
 * landed: "related records" for a subject the anchor set cannot name yet, and no `where`
 * clause for an inner condition whose field it cannot resolve. A shorter true sentence
 * beats a longer one built out of ids.
 */
function describeRelated(
  node: ViewRelated,
  relations: RelationContext,
  onObjectId: string,
): FilterSentence {
  const anchor = relations
    .anchorsOf(onObjectId)
    ?.find((entry) => entry.anchorFieldId === node.anchorFieldId);
  const counting = node.quantifier === 'count' ? node.count : undefined;
  const singular = counting !== undefined && counting.n === 1;
  const subject =
    anchor === undefined ? 'related records' : singular ? anchor.labelSingular : anchor.label;

  const child = node.children[0];
  /**
   * THE `where` CLAUSE IS THE OPERAND OF A RELATED SENTENCE, which is why it is carried
   * apart from the subject rather than concatenated into it: in a token the subject ("Has
   * any Deals") is the head a reader needs and the clause is what may end in an ellipsis.
   */
  let where: string | undefined;
  if (node.children.length === 1 && child !== undefined && anchor !== undefined) {
    if (child.kind === 'related') {
      /**
       * ITS FIRST LETTER, not the whole sentence. `toLowerCase()` on the nested sentence
       * also lowercased the OPERANDS inside it - "where city is berlin" - which is a value
       * a person typed being rewritten on the way to a chip.
       */
      where = `where ${uncapitalise(
        sentenceOf(describeRelated(child, relations, anchor.relatedObjectId)),
      )}`;
    } else if (child.kind === 'condition') {
      const innerField = relations
        .fieldsOf(anchor.relatedObjectId)
        ?.find((field) => field.id === child.fieldId);
      if (innerField !== undefined)
        where = `where ${sentenceOf(describeCondition(child, innerField))}`;
    }
  }

  /**
   * A FORWARD HOP IS A SINGLE RECORD and reads like one: "Agency where City is Berlin",
   * which is the sentence section 11 writes for it. "Has any Agency" would be true and
   * would sound like a to-many question about a to-one reference.
   */
  const forward = anchor?.direction === 'forward' || node.direction === 'forward';
  if (forward && node.quantifier === 'any') {
    return where === undefined
      ? { head: `${subject} is set`, value: undefined }
      : { head: subject, value: where };
  }
  if (node.quantifier === 'any') return { head: `Has any ${subject}`, value: where };
  if (node.quantifier === 'none') return { head: `Has no ${subject}`, value: where };
  if (counting === undefined) return { head: `Has any ${subject}`, value: where };
  const phrase = (Object.keys(PHRASE_COMPARATORS) as CountingPhrase[]).find(
    (candidate) => PHRASE_COMPARATORS[candidate] === counting.cmp,
  );
  const word = phrase === undefined ? `not exactly` : PHRASE_WORDS[phrase];
  return { head: `Has ${word} ${counting.n} ${subject}`, value: where };
}

function uncapitalise(sentence: string): string {
  return `${sentence.charAt(0).toLowerCase()}${sentence.slice(1)}`;
}

function describeCondition(
  node: Extract<ViewFilterNode, { kind: 'condition' }>,
  field: FieldMetadata,
): FilterSentence {
  const operator = OPERATOR_LABELS[node.operator];
  const head = `${field.label} ${operator}`;
  if (isNullaryOperator(node.operator)) return { head, value: undefined };
  if (isRelativeDateOperand(node.value)) {
    return {
      head: `${field.label} is`,
      value: RELATIVE_DATE_LABELS[node.value.relative].toLowerCase(),
    };
  }
  if (Array.isArray(node.value)) {
    const labels = node.value.map((entry) => optionLabel(field, entry));
    return { head, value: labels.join(', ') };
  }
  if (fieldShape(field.type) === 'relation') return { head, value: 'one record' };
  return { head, value: optionLabel(field, node.value) };
}

function optionLabel(field: FieldMetadata, value: unknown): string {
  const option = field.options?.selectOptions?.find((entry) => entry.id === value);
  return option?.label ?? String(value);
}

export { RELATIVE_DATE_TOKENS };
